// lcd.js — 640x80 1bpp built-in MSM6255 LCD monitor renderer.
//
// This is the machine's built-in monitor and where the boot welcome banner is
// drawn. Layout is dead simple: linear, 80 bytes/row, bit7 =
// leftmost pixel, 1 = pixel on, at guest 0x80000:
//
//   byte    = HEAPU8[memBase + 0x80000 + y*80 + (x>>3)]
//   pixelOn = (byte >> (7-(x&7))) & 1
//
// The active area is 640x80 cells at 0.38 mm pitch. Each energised cell is a
// centred 0.34 mm dot; display.css applies that 17/19 mask to this transparent
// ink layer. Unenergised cells expose one continuous sheet of reflective glass.

import { ScaledPresenter } from './blit.js';

const LCD_ADDR = 0x80000;
const LCD_W = 640;
const LCD_H = 80;
const LCD_STRIDE = LCD_W >> 3; // 80 bytes/row

// ---- palette: reflective monochrome STN LCD, dark-on-light ------------
// Reference: period MSM6255-class STN panels are REFLECTIVE — no backlight,
// no emissive glow. The cell itself is a light greenish-gray, and pixels
// darken (do not light up) when energised. This is the inverse of a CRT's
// dark-glass-glowing-phosphor look, which is why it must NOT share crt.js's
// palette even though the blit loop below is structurally identical.
const LCD_FG = [0x1e, 0x24, 0x30]; // dark blue-black ink (energised pixel)

export class LcdRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.presenter = new ScaledPresenter(canvas, { alpha: true });
    this.image = new ImageData(LCD_W, LCD_H);
    this.lit = 0;
    this.presenter.clear();
  }

  render(machine) {
    const heap = machine.heap();
    const base = machine.memBase() + LCD_ADDR;
    const px = this.image.data;
    let lit = 0;
    for (let y = 0; y < LCD_H; y++) {
      const rowByte = base + y * LCD_STRIDE;
      let outRow = y * LCD_W * 4;
      for (let bx = 0; bx < LCD_STRIDE; bx++) {
        const v = heap[rowByte + bx];
        const x0 = bx << 3;
        for (let bit = 0; bit < 8; bit++) {
          const on = (v >> (7 - bit)) & 1;
          const o = outRow + (x0 + bit) * 4;
          if (on) {
            px[o] = LCD_FG[0]; px[o + 1] = LCD_FG[1]; px[o + 2] = LCD_FG[2]; lit++;
            px[o + 3] = 255;
          } else {
            px[o] = 0; px[o + 1] = 0; px[o + 2] = 0;
            px[o + 3] = 0;
          }
        }
      }
    }
    this.presenter.present(this.image);
    this.lit = lit;
    return lit;
  }

  // POWER OFF: clear the ink layer so only the bare reflective glass remains.
  powerOff() {
    this.presenter.clear();
    this.lit = 0;
  }
}
